import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  // Swap the active token for a short-lived impersonation token. The current
  // (admin) token is stashed so `stopImpersonating()` can restore it.
  const impersonate = (impersonationToken, targetUser) => {
    const current = localStorage.getItem('token');
    if (current) localStorage.setItem('original_token', current);
    localStorage.setItem('token', impersonationToken);
    setToken(impersonationToken);
    setUser(targetUser);
  };
  // Restore the admin token saved by `impersonate()`. Returns true on success.
  const stopImpersonating = () => {
    const original = localStorage.getItem('original_token');
    if (!original) return false;
    localStorage.removeItem('original_token');
    localStorage.setItem('token', original);
    setToken(original);
    return true;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('original_token');
    setToken(null);
    setUser(null);
  };

  // Map NAV item ids (hyphenated) to the permissions tab keys (snake_case
  // on the backend). Centralized here so consumers can just call `can()`.
  const TAB_KEY_FOR_NAV = {
    'menu': null, // menu landing is always allowed for staff
    'new-order': 'new_order',
    'orders': 'orders',
    'dashboard': 'dashboard',
    'product-summary': 'product_summary',
    'products': 'products',
    'dealers': 'dealers',
    'dealer-crm': 'dealer_crm',
    'reports': 'reports',
    'transport': 'transport',
    'stock': 'stock',
    'planning': 'planning',
    'production': 'production',
    'users': 'users',
  };
  /**
   * Permission helper. Usage:
   *   can('orders', 'view')     → boolean
   *   can('orders')             → defaults to 'view'
   *   canNav('orders')          → boolean (accepts NAV id like 'new-order')
   * Returns true when the user is unauthenticated (gating only kicks in once
   * the user is loaded) to avoid flicker on first paint.
   */
  const can = (tabKey, action = 'view') => {
    if (!user) return true;
    const peff = user.permissions_effective || {};
    const t = peff[tabKey];
    return !!(t && t[action]);
  };
  const canNav = (navId) => {
    // Product Costing is strictly Admin-only (no per-role permission matrix).
    if (navId === 'costing') return !user || user.role === 'admin';
    const k = TAB_KEY_FOR_NAV[navId];
    if (k === null || k === undefined) return true;
    return can(k, 'view');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, can, canNav, impersonate, stopImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};